import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

interface AppError {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  timestamp: number;
  details?: any;
}

interface ErrorState {
  errors: AppError[];
  lastError: AppError | null;
  // Simple error state for compatibility with existing code
  message: string | null;
  details?: any;
}

const initialState: ErrorState = {
  errors: [],
  lastError: null,
  message: null,
  details: undefined,
};

const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    addError: (state, action: PayloadAction<Omit<AppError, 'id' | 'timestamp'>>) => {
      const error: AppError = {
        ...action.payload,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
      };
      state.errors.push(error);
      state.lastError = error;
      
      // Keep only last 50 errors
      if (state.errors.length > 50) {
        state.errors = state.errors.slice(-50);
      }
    },
    removeError: (state, action: PayloadAction<string>) => {
      state.errors = state.errors.filter((error) => error.id !== action.payload);
      if (state.lastError?.id === action.payload) {
        state.lastError = null;
      }
    },
    clearErrors: (state) => {
      state.errors = [];
      state.lastError = null;
      state.message = null;
      state.details = undefined;
    },
    // Simple error setters for compatibility
    setError: (state, action: PayloadAction<{ message: string; details?: any }>) => {
      state.message = action.payload.message;
      state.details = action.payload.details;
      
      // Also add to errors array for consistency
      const error: AppError = {
        id: crypto.randomUUID(),
        message: action.payload.message,
        type: 'error',
        timestamp: Date.now(),
        details: action.payload.details,
      };
      state.errors.push(error);
      state.lastError = error;
      
      // Keep only last 50 errors
      if (state.errors.length > 50) {
        state.errors = state.errors.slice(-50);
      }
    },
  },
});

export const { addError, removeError, clearErrors, setError } = errorSlice.actions;
export default errorSlice.reducer;